import React, { ReactElement, useState } from 'react'
import {
  Menu,
  MenuProps,
  MenuItem,
  ButtonOwnProps,
  Tooltip,
  IconButton
} from '@mui/material'
import ButtonBase from 'design-system/button'

/**
 * BaseMenuButtonProps defines the properties for the MenuButton component.
 * It includes options for the menu items, a callback for selection, and optional start icon.
 * It extends the MUI MenuProps to allow customization of the menu.
 */
export interface BaseMenuButtonProps
  extends Omit<MenuProps, 'anchorEl' | 'open' | 'onClose' | 'children'> {
  options: Array<{ label: string; value: any }>
  onSelect?: (value: any) => void
  startIcon?: ButtonOwnProps['startIcon']
  value?: any
}

/** MenuButtonLabelProps defines the properties for the MenuButton component when using a label. */
type MenuButtonLabelProps = BaseMenuButtonProps & {
  label: string
}

/** MenuButtonIconProps defines the properties for the MenuButton component when using an icon. */
type MenuButtonIconProps = BaseMenuButtonProps & {
  icon: ReactElement
}

/** MenuButton component
 * This component renders a button that opens a menu with options.
 * It can be used with either a label or an icon.
 * @param {MenuButtonLabelProps | MenuButtonIconProps} props - Properties for the MenuButton
 * @returns JSX.Element
 */
const MenuButton: React.FC<MenuButtonLabelProps | MenuButtonIconProps> = ({
  options,
  value,
  onSelect,
  startIcon,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open: boolean = !!anchorEl

  let buttonComponent: JSX.Element

  const isLabel = 'label' in props

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const onSelectItem = (value: any) => {
    if (onSelect) onSelect(value)
    handleClose()
  }

  if ('label' in props) {
    const { label } = props
    const buttonLabel = !value
      ? label
      : (options.find(({ value: optionValue }) => optionValue === value)
          ?.label ?? label)

    buttonComponent = (
      <ButtonBase
        variant='contained'
        onClick={handleClick}
        startIcon={startIcon}
        sx={{
          flex: 1,
          gap: 0
        }}
      >
        {buttonLabel}
      </ButtonBase>
    )
  } else {
    buttonComponent = (
      <IconButton onClick={handleClick}>{props.icon}</IconButton>
    )
  }

  return (
    <>
      {buttonComponent}
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} {...props}>
        {options.map(({ label, value: oValue }, index) => {
          const isSelected = value === oValue
          return (
            <Tooltip key={index} title={label} placement='right'>
              <MenuItem
                key={`menu-item-${index}`}
                onClick={() => onSelectItem(isSelected ? undefined : oValue)}
                sx={{
                  backgroundColor: isSelected
                    ? 'action.selected'
                    : undefined,
                  width: 'auto'
                }}
              >
                {label}
              </MenuItem>
            </Tooltip>
          )
        })}
      </Menu>
    </>
  )
}

export default MenuButton
